import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer, SlotInfo } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { pl } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { toast } from '../../cabinet/components/ui/Toast';
import { getCalendarEvents, generateCalendarSlots, deleteCalendarEvent, assignPatientToEvent, cancelEventKeepSlot } from '../api/dietitianApi';

const locales = {
  'pl': pl,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

/**
 * Парсить ISO дату з Warsaw офсетом (+02:00 / +01:00) і повертає Date
 * так, ніби вона є локальною — без конвертації в timezone браузера.
 *
 * react-big-calendar рендерить події по локальному часу Date об'єкта,
 * тому якщо зробити new Date("2026-08-21T13:00:00+02:00") в браузері з UTC+3
 * — отримаємо 14:00. Щоб завжди показувати Warsaw час — беремо тільки
 * "wall clock" частину (рік, місяць, день, година, хвилина) і ігноруємо offset.
 *
 * Наприклад: "2026-08-21T13:00:00+02:00" → Date(2026, 7, 21, 13, 0) локально.
 */
function parseDateAsWarsaw(isoString: string): Date {
  // Відрізаємо offset (+02:00 або Z) і парсимо як "local"
  // Формати: "2026-08-21T13:00:00+02:00" або "2026-08-21T13:00:00Z"
  const clean = isoString.replace(/([+-]\d{2}:\d{2}|Z)$/, '');
  return new Date(clean);
}

export const CalendarPage: React.FC<{ onOpenOrder: (id: number) => void; isEditable?: boolean }> = ({ onOpenOrder, isEditable = true }) => {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Calendar state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 860);
  const [view, setView] = useState<any>(isMobile ? 'day' : 'week');
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 860);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Modals
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateData, setGenerateData] = useState<{ start: Date, end: Date } | null>(null);
  const [duration, setDuration] = useState(30);

  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [patientName, setPatientName] = useState('');

  const loadEvents = async () => {
    setLoading(true);
    try {
      const res = await getCalendarEvents();
      const mapped = (res.events || []).map((e: any) => ({
        ...e,
        // Парсимо дату без конвертації браузерного часового поясу.
        // API повертає час у Warsaw (Europe/Warsaw = UTC+2 влітку).
        // new Date() конвертує в локальний час браузера (наприклад, UTC+3 для України).
        // Щоб завжди показувати Warsaw час — прибираємо offset і парсимо як "local".
        start: parseDateAsWarsaw(e.start),
        end: parseDateAsWarsaw(e.end)
      }));
      setEvents(mapped);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    setGenerateData({ start: slotInfo.start, end: slotInfo.end });
    setShowGenerateModal(true);
  };

  const handleGenerateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!generateData) return;
    setLoading(true);
    setShowGenerateModal(false);

    try {
      const date = format(generateData.start, 'yyyy-MM-dd');
      const start_time = format(generateData.start, 'HH:mm');
      const end_time = format(generateData.end, 'HH:mm');
      await generateCalendarSlots({ date, start_time, end_time, duration });
      await loadEvents();
    } catch (err) {
      toast('Błąd podczas generowania slotów.');
      setLoading(false);
    }
  };

  const handleSelectEvent = (event: any) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent || !window.confirm('Czy na pewno chcesz usunąć ten slot?')) return;
    setLoading(true);
    setShowEventModal(false);
    try {
      await deleteCalendarEvent(selectedEvent.id);
      await loadEvents();
    } catch (e) {
      toast('Błąd podczas usuwania.');
      setLoading(false);
    }
  };

  const handleCancelEvent = async () => {
    if (!selectedEvent || !window.confirm('Czy na pewno chcesz anulować wizytę i zwolnić ten termin?')) return;
    setLoading(true);
    setShowEventModal(false);
    try {
      await cancelEventKeepSlot(selectedEvent.id);
      await loadEvents();
      toast('Wizyta anulowana, termin zwolniony.');
    } catch (e) {
      toast('Błąd podczas anulowania.');
      setLoading(false);
    }
  };

  const handleAssignPatient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent || !patientName) return;
    setLoading(true);
    setShowEventModal(false);
    try {
      await assignPatientToEvent(selectedEvent.id, patientName);
      setPatientName('');
      await loadEvents();
    } catch (e) {
      toast('Błąd podczas przypisywania pacjenta.');
      setLoading(false);
    }
  };

  const eventStyleGetter = (event: any) => {
    let style = { backgroundColor: '#F1F3F4', color: '#5F6368', border: '1px solid #DADCE0' };
    
    if (event.title === '[Slot]') {
      style = { backgroundColor: '#fcfffd', color: '#137333', border: '1px solid #CEEAD6' };
    } else if (event.title?.includes('Konsultacja') || event.title?.includes('[Візит]')) {
      if (event.isReady) {
        style = { backgroundColor: '#d6f3e6', color: '#15734e', border: '1px solid #b7e8d3' };
      } else {
        style = { backgroundColor: '#E8F0FE', color: '#1967D2', border: '1px solid #D2E3FC' };
      }
    }
    
    return {
      style: {
        ...style,
        borderRadius: '4px',
        opacity: 0.9,
        fontSize: '12px',
        padding: '2px 4px',
        display: 'block'
      }
    };
  };

  return (
    <div>
      <div>
        <h1>{isEditable ? 'Grafik dietetyka' : 'Mój kalendarz'}</h1>
        {!isEditable && (
          <div className="muted" style={{ marginBottom: 8 }}>Tylko do podglądu · grafik ustawia administrator</div>
        )}
      </div>
      <div style={{ height: 14 }}></div>

      <div style={{ position: 'relative' }}>
        {loading && (
          <div style={styles.loadingOverlay}>
            <div style={{ background: '#fff', padding: '12px 24px', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>Ładowanie...</div>
          </div>
        )}
        
        <div style={{ height: 700, background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #E6D6FA' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            selectable={isEditable}
            onSelectSlot={isEditable ? handleSelectSlot : undefined}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            view={view}
            onView={setView}
            views={isMobile ? ['month', 'day', 'agenda'] : ['month', 'week', 'day', 'agenda']}
            date={date}
            onNavigate={setDate}
            culture="pl"
            dayLayoutAlgorithm="no-overlap"
            messages={{
              next: "Następny",
              previous: "Poprzedni",
              today: "Dziś",
              month: "Miesiąc",
              week: "Tydzień",
              day: "Dzień",
              agenda: "Agenda"
            }}
            min={new Date(2020, 1, 1, 8, 0)} // Start at 8 AM
            max={new Date(2020, 1, 1, 21, 0)} // End at 9 PM
          />
        </div>
      </div>

      {/* Modal for Generation */}
      {showGenerateModal && generateData && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ marginTop: 0 }}>Generuj sloty</h3>
            <p style={{ fontSize: 13, color: '#666' }}>
              Od: {format(generateData.start, 'yyyy-MM-dd HH:mm')}<br/>
              Do: {format(generateData.end, 'HH:mm')}
            </p>
            <form onSubmit={handleGenerateSubmit} style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Długość jednego slotu (minuty)</label>
                <select value={duration} onChange={e => setDuration(Number(e.target.value))} style={styles.input}>
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowGenerateModal(false)} style={styles.btnCancel}>Anuluj</button>
                <button type="submit" style={styles.btnSubmit}>Generuj</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for Event Action */}
      {showEventModal && selectedEvent && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3 style={{ marginTop: 0, marginBottom: 8 }}>Szczegóły wydarzenia</h3>
              <button onClick={() => setShowEventModal(false)} style={styles.closeBtn}>×</button>
            </div>
            
            <p style={{ fontSize: 14, fontWeight: 600 }}>{selectedEvent.title === '[Slot]' ? 'Wolny termin' : selectedEvent.title}</p>
            <p style={{ fontSize: 13, color: '#666' }}>
              {format(selectedEvent.start, 'yyyy-MM-dd HH:mm')} - {format(selectedEvent.end, 'HH:mm')}
            </p>
            {selectedEvent.description && (
              <div style={{ fontSize: 13, color: '#444', background: '#f5f5f5', padding: 8, borderRadius: 4, marginTop: 8 }}>
                {selectedEvent.description}
              </div>
            )}

            {selectedEvent.title === '[Slot]' ? (
              <div style={{ marginTop: 20 }}>
                {isEditable ? (
                  <>
                    <form onSubmit={handleAssignPatient}>
                      <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>Zapisz pacjenta ręcznie</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input 
                          required
                          placeholder="Imię i nazwisko / telefon" 
                          value={patientName} 
                          onChange={e => setPatientName(e.target.value)} 
                          style={styles.input} 
                        />
                        <button type="submit" style={styles.btnAssign}>Zapisz</button>
                      </div>
                    </form>
                    <hr style={{ margin: '20px 0', border: 'none', borderTop: '1px solid #eee' }} />
                    <button onClick={handleDeleteEvent} style={styles.btnDelete}>Usuń wolny termin</button>
                  </>
                ) : (
                  <p style={{ fontSize: 13, color: '#888' }}>Ten termin jest wolny. Pacjent może go zarezerwować.</p>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 20 }}>
                {selectedEvent.description?.match(/Zamówienie #(\d+)/) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <button onClick={() => onOpenOrder(parseInt(selectedEvent.description.match(/Zamówienie #(\d+)/)[1], 10))} style={styles.btnAssign}>
                      Otwórz wizytę
                    </button>
                    {isEditable && (
                      <button onClick={handleCancelEvent} style={styles.btnWarning}>Anuluj wizytę (zwolnij termin)</button>
                    )}
                    {isEditable && (
                      <button onClick={handleDeleteEvent} style={styles.btnDelete}>Usuń i anuluj wizytę</button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Wydarzenie zapisane ręcznie lub przez Google Calendar.</p>
                    {isEditable && (
                      <button onClick={handleDeleteEvent} style={styles.btnDelete}>Usuń wydarzenie</button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  wrap: { maxWidth: 1000, margin: '0 auto', padding: '20px 0' } as React.CSSProperties,
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  loadingOverlay: { position: 'absolute' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', padding: 24, borderRadius: 12, width: '100%', maxWidth: 400, boxShadow: '0 10px 30px rgba(0,0,0,0.1)' },
  input: { flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, outline: 'none' },
  btnCancel: { padding: '8px 16px', background: 'transparent', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer' },
  btnSubmit: { padding: '8px 16px', background: '#361777', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' },
  btnAssign: { padding: '8px 16px', background: '#137333', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' },
  btnWarning: { padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', width: '100%' },
  btnDelete: { padding: '8px 16px', background: '#d93025', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', width: '100%' },
  closeBtn: { background: 'none', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#999', padding: 0 }
};
